import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import {
  useAnswerQuestion,
  usePauseSurvey,
  useSkipQuestion,
  useSurvey,
  useSurveyBack,
} from '@/shared/api/queries'
import type { SurveyPipelineResult, SurveyQuestion, SurveyState } from '@/shared/api/queries'
import { useLocalization } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import { useBackButton } from '@/shared/telegram'
import {
  Alert,
  Button,
  Card,
  Icon,
  Progress,
  Skeleton,
  Stack,
  Stage,
  StateBlock,
  Text,
} from '@/shared/ui'

import { Answer, isAnswered } from './answers'
import type { AnswerValue } from './answers'
import { forgetAnswers, loadAnswer, saveAnswer } from './draft'
import { ReadingScreen } from './ReadingScreen'
import { useReading } from './reading'
import './survey.css'

const MONTH_LENGTH = 7

function answerOf(question: SurveyQuestion): AnswerValue | undefined {
  if (!question.answered || question.skipped) {
    return undefined
  }

  return question.answer as AnswerValue | undefined
}

function offered(
  question: SurveyQuestion,
  value: AnswerValue | undefined,
): AnswerValue | undefined {
  const options = question.options

  if (value === undefined || !options || options.length === 0) {
    return value
  }

  const known = (candidate: string) => options.some((option) => option.value === candidate)

  if (Array.isArray(value)) {
    const kept = value.filter(known)

    return kept.length > 0 ? kept : undefined
  }

  return typeof value === 'string' && !known(value) ? undefined : value
}

function shaped(question: SurveyQuestion, value: AnswerValue | undefined): AnswerValue | undefined {
  if (question.type === 'month' && typeof value === 'string') {
    return value.slice(0, MONTH_LENGTH)
  }

  return value
}

function initialOf(survey: string, question: SurveyQuestion): AnswerValue | undefined {
  const remembered =
    loadAnswer(survey, question.tag) ??
    answerOf(question) ??
    (question.default as AnswerValue | undefined)

  return shaped(question, offered(question, remembered))
}

export type Extra = (
  question: SurveyQuestion,
  value: AnswerValue | undefined,
  onChange: (value: AnswerValue | undefined) => void,
) => ReactNode

export type Suggest = (question: SurveyQuestion) => string[]

export type Guard = (
  question: SurveyQuestion,
  value: AnswerValue,
  proceed: (instead?: AnswerValue) => void,
) => boolean

// Asking is one question, and it is keyed by the question's tag so that moving
// on gives it a fresh state rather than the previous answer with a new label.
// The draft is read once, on the way in, and written on every keystroke.
function Asking({
  survey,
  state,
  question,
  busy,
  failure,
  extra,
  suggest,
  onAnswer,
  onSkip,
  onClose,
}: {
  survey: string
  state: SurveyState
  question: SurveyQuestion
  busy: boolean
  failure: Error | null
  extra?: Extra | undefined
  suggest?: Suggest | undefined
  onAnswer: (value: AnswerValue) => void
  onSkip: () => void
  onClose: () => void
}) {
  const { t } = useLocalization()
  const [value, setValue] = useState<AnswerValue | undefined>(() => initialOf(survey, question))

  useEffect(() => {
    saveAnswer(survey, question.tag, value)
  }, [survey, question.tag, value])

  const given = isAnswered(value)
  const { answered, remaining } = state.progress
  const picture = question.image ? `${import.meta.env.BASE_URL}${question.image}` : undefined
  const large = Boolean(picture) && question.image_size === 'large'

  const head = (
    <Stack gap="sm" className="ask__head">
      <Text role="eyebrow" tone="muted">
        {state.title}
      </Text>
      <Text as="h1" role="heading-2">
        {question.text}
      </Text>
      {question.hint ? (
        <Text role="caption" tone="muted" measure>
          {question.hint}
        </Text>
      ) : null}
    </Stack>
  )

  const body = (
    <div className="ask__body">
      <Answer
        question={question}
        value={value}
        onChange={setValue}
        suggested={suggest?.(question)}
      />
      {extra ? extra(question, value, setValue) : null}
    </div>
  )

  return (
    <Stage
      top
      rail={
        <div className="ask__rail">
          <div className="ask__progress">
            <Progress
              steps
              value={answered}
              max={answered + remaining}
              label={state.title}
              caption={t('survey.progress', {
                done: Math.min(answered + 1, answered + remaining),
                total: answered + remaining,
              })}
            />
          </div>
          {state.can_close ? (
            <button
              className="ask__close"
              type="button"
              aria-label={t('survey.close')}
              disabled={busy}
              onClick={onClose}
            >
              <span aria-hidden="true">✕</span>
            </button>
          ) : null}
        </div>
      }
      footer={
        <>
          {failure ? <Alert status="danger" title={failure.message} /> : null}
          <Button full disabled={!given || busy} onClick={() => given && onAnswer(value!)}>
            {busy ? t('survey.saving') : t('survey.continue')}
          </Button>
          {question.can_skip ? (
            <Button size="sm" variant="quiet" disabled={busy} onClick={onSkip}>
              {t('survey.skip')}
            </Button>
          ) : null}
        </>
      }
    >
      {picture && !large ? <img className="ask__art" src={picture} alt="" /> : null}

      {head}

      {large ? (
        <div className="ask__scene">
          <div className="ask__art--large">
            <img src={picture} alt="" />
          </div>
          {body}
        </div>
      ) : (
        body
      )}
    </Stage>
  )
}

function Read({
  result,
  onOpen,
}: {
  result: SurveyPipelineResult | undefined
  onOpen: () => void
}) {
  const { t } = useLocalization()
  const missing = result?.missing ?? []

  return (
    <Stage
      footer={
        <Button full onClick={onOpen}>
          {t('documents.upload.open')}
        </Button>
      }
    >
      <Card>
        <Stack gap="sm">
          <Icon size="lg">✅</Icon>
          <Text as="h1" role="heading-2">
            {t('documents.upload.added')}
          </Text>
          {missing.length > 0 ? (
            <Text tone="muted" measure>
              {t('survey.pipeline.missing', {
                fields: missing
                  .map((key) => t(`documents.field.${key}` as TranslationKey))
                  .join(', '),
              })}
            </Text>
          ) : null}
        </Stack>
      </Card>
    </Stage>
  )
}

function Finished({ state, onLeave }: { state: SurveyState; onLeave: () => void }) {
  const { t } = useLocalization()

  return (
    <Stage
      footer={
        <Button full onClick={onLeave}>
          {t('survey.done.cta')}
        </Button>
      }
    >
      <Card>
        <Stack gap="sm">
          <Icon size="lg">✅</Icon>
          <Text as="h1" role="heading-2">
            {t('survey.done.title')}
          </Text>
          <Text tone="muted" measure>
            {t('survey.done.body', { survey: state.title })}
          </Text>
        </Stack>
      </Card>
    </Stage>
  )
}

function nothing() {}

export function SurveyRunner({
  tag,
  onLeave,
  onDocument = nothing,
  onQuota = nothing,
  onBeforeStart,
  onCompleted,
  extra,
  suggest,
  guard,
}: {
  tag: string
  onLeave: () => void
  onDocument?: (documentId: string) => void
  onQuota?: () => void
  onBeforeStart?: (() => void) | undefined
  onCompleted?: ((state: SurveyState) => void) | undefined
  extra?: Extra | undefined
  suggest?: Suggest | undefined
  guard?: Guard | undefined
}) {
  const survey = useSurvey(tag)
  const reading = useReading(tag, onQuota)
  const answer = useAnswerQuestion()
  const skip = useSkipQuestion()
  const back = useSurveyBack()
  const pause = usePauseSurvey()

  const state = survey.data
  const done = state?.status === 'completed'

  useEffect(() => {
    if (done) {
      forgetAnswers(tag)
    }
  }, [tag, done])

  const told = useRef(false)

  useEffect(() => {
    if (!done || !state || !onCompleted || told.current) {
      return
    }

    told.current = true
    onCompleted(state)
  }, [done, state, onCompleted])

  const stepBack = () => back.mutate(tag)

  useBackButton(backward())

  const busy = answer.isPending || skip.isPending || back.isPending || pause.isPending
  const documentId = state?.outputs?.document_id

  return (
    <StateBlock
      pending={survey.isPending}
      error={survey.error}
      onRetry={() => void survey.refetch()}
      skeleton={<Skeleton variant="row" rows={4} title />}
    >
      {state && state.pipeline && !state.question ? (
        <ReadingScreen
          state={state}
          pipeline={state.pipeline}
          reading={reading}
          busy={busy}
          onBack={stepBack}
        />
      ) : state && state.question ? (
        <Asking
          key={state.question.tag}
          survey={tag}
          state={state}
          question={state.question}
          busy={busy}
          failure={answer.error ?? skip.error ?? null}
          extra={extra}
          suggest={suggest}
          onAnswer={(value) => submit(state.question!, value)}
          onSkip={() => skip.mutate({ tag, question: state.question!.tag }, { onSuccess: proceed })}
          onClose={() => pause.mutate(tag, { onSuccess: onLeave })}
        />
      ) : state && done && documentId ? (
        <Read result={reading.result} onOpen={() => onDocument(documentId)} />
      ) : state && done && onCompleted ? (
        <Skeleton variant="row" rows={4} title />
      ) : state ? (
        <Finished state={state} onLeave={onLeave} />
      ) : null}
    </StateBlock>
  )

  function backward(): (() => void) | undefined {
    if (!state || done) {
      return state?.can_close === false ? undefined : onLeave
    }

    if (!state.can_go_back) {
      return undefined
    }

    if (state.progress.answered > 0) {
      return stepBack
    }

    return onBeforeStart ?? (state.can_close ? onLeave : undefined)
  }

  function submit(question: SurveyQuestion, value: AnswerValue) {
    const send = (instead: AnswerValue = value) =>
      answer.mutate({ tag, question: question.tag, value: instead }, { onSuccess: proceed })

    if (guard?.(question, value, send)) {
      return
    }

    send(value)
  }

  // reset clears the refusal of the previous question: an answer the server
  // would not take is about that question, and carrying the message onto the
  // next one would be telling somebody their new answer is wrong.
  function reset() {
    answer.reset()
    skip.reset()
  }

  function proceed(next: SurveyState) {
    reset()

    if (next.pipeline && !next.question) {
      reading.read(next.pipeline)
    }
  }
}
